import { useState, useEffect } from 'react';
import { Package, TrendingUp, Bot, Truck, ToggleLeft, ToggleRight, CheckCircle2 } from 'lucide-react';
import { api } from '../../../api/client';

interface DataSource {
  provider: string;
  priority: number;
  active: boolean;
  description?: string;
}

interface SourceGroup {
  processKey: string;
  processName: string;
  icon: typeof Package;
  color: string;
  sources: DataSource[];
}

export function DataSourcesTab({ t }: { t: (k: string) => string }) {
  const sourceGroups: SourceGroup[] = [
    {
      processKey: 'enrichment',
      processName: t('settings.sourcesEnrichment'),
      icon: Package,
      color: 'text-blue-500',
      sources: [
        { provider: 'Amazon Product API', priority: 1, active: true, description: 'getOffers, getItems, getProductSearch' },
        { provider: 'EasyParser', priority: 2, active: true, description: 'HTML scraping fallback' },
        { provider: 'Rainforest API', priority: 3, active: false, description: '0 credits remaining' },
      ],
    },
    {
      processKey: 'price_monitor',
      processName: t('settings.sourcesPriceMonitor'),
      icon: TrendingUp,
      color: 'text-orange-500',
      sources: [
        { provider: 'Amazon Product API', priority: 1, active: true, description: 'Real-time pricing & availability' },
        { provider: 'EasyParser', priority: 2, active: true, description: 'Fallback for API failures' },
      ],
    },
    {
      processKey: 'ai_generation',
      processName: t('settings.sourcesAIGeneration'),
      icon: Bot,
      color: 'text-purple-500',
      sources: [
        { provider: 'OpenAI GPT-4.1', priority: 1, active: true, description: 'Title, description, attributes generation' },
        { provider: 'Claude (Anthropic)', priority: 2, active: true, description: 'Fallback & quality review' },
        { provider: 'Perplexity Sonar', priority: 3, active: true, description: 'Web search for Q&A' },
      ],
    },
    {
      processKey: 'tracking',
      processName: t('settings.sourcesTracking'),
      icon: Truck,
      color: 'text-cyan-500',
      sources: [
        { provider: 'KD100', priority: 1, active: true, description: 'International carrier tracking' },
        { provider: 'Amazon Reports', priority: 2, active: true, description: 'Order shipment reports' },
        { provider: 'Amazon Ordering API', priority: 3, active: true, description: 'Direct tracking by external_id' },
      ],
    },
  ];

  const [toggles, setToggles] = useState<Record<string, boolean>>(() => {
    const init: Record<string, boolean> = {};
    sourceGroups.forEach(g => g.sources.forEach(s => {
      init[`${g.processKey}:${s.provider}`] = s.active;
    }));
    return init;
  });

  // Map composite key -> DB id for PUT calls
  const [dbIds, setDbIds] = useState<Record<string, number>>({});
  const [saving, setSaving] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);

  // Load from backend on mount
  useEffect(() => {
    api.get<Record<string, Array<{ id: number; provider: string; enabled: boolean; process_key: string }>>>('/datasources').then(res => {
      if (res.success && res.data) {
        const data = res.data as any;
        const newToggles: Record<string, boolean> = {};
        const newIds: Record<string, number> = {};
        for (const processKey of Object.keys(data)) {
          for (const src of data[processKey]) {
            const key = `${processKey}:${src.provider}`;
            newToggles[key] = src.enabled;
            newIds[key] = src.id;
          }
        }
        setToggles(prev => ({ ...prev, ...newToggles }));
        setDbIds(prev => ({ ...prev, ...newIds }));
      }
    });
  }, []);

  const handleToggle = async (groupKey: string, provider: string) => {
    const key = `${groupKey}:${provider}`;
    const newVal = !toggles[key];
    setToggles(prev => ({ ...prev, [key]: newVal }));

    const id = dbIds[key];
    if (!id) return;

    setSaving(key);
    const res = await api.put(`/datasources/${id}`, { enabled: newVal });
    setSaving(null);
    if (res.success) {
      setToast(t('settings.sourcesSaved'));
      setTimeout(() => setToast(null), 2000);
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-lg font-semibold text-[var(--color-text)]">{t('settings.sourcesTitle')}</h2>
        <p className="text-sm text-[var(--color-text-muted)] mt-1">{t('settings.sourcesDesc')}</p>
      </div>

      {toast && (
        <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-green-500/10 text-green-600 text-xs font-medium border border-green-500/20">
          <CheckCircle2 className="w-3.5 h-3.5" />
          {toast}
        </div>
      )}

      <div className="space-y-4">
        {sourceGroups.map(group => {
          const GroupIcon = group.icon;
          return (
            <div key={group.processKey} className="bg-[var(--color-bg)] border border-[var(--color-border)] rounded-lg overflow-hidden">
              {/* Group header */}
              <div className="px-4 py-3 border-b border-[var(--color-border)] bg-[var(--color-surface)]">
                <h3 className={`text-sm font-semibold flex items-center gap-2 ${group.color}`}>
                  <GroupIcon className="w-4 h-4" />
                  {group.processName}
                </h3>
              </div>

              {/* Sources */}
              <div className="divide-y divide-[var(--color-border)]">
                {group.sources.map(source => {
                  const key = `${group.processKey}:${source.provider}`;
                  const isActive = toggles[key];
                  return (
                    <div key={source.provider} className="flex items-center gap-3 px-4 py-3 hover:bg-[var(--color-surface-hover)] transition-colors">
                      {/* Priority badge */}
                      <span className={`w-7 h-7 rounded-full flex items-center justify-center text-[11px] font-bold shrink-0 ${
                        isActive
                          ? 'bg-[var(--color-primary-light)] text-[var(--color-primary)]'
                          : 'bg-[var(--color-border)] text-[var(--color-text-muted)]'
                      }`}>
                        {source.priority}
                      </span>

                      {/* Provider info */}
                      <div className="flex-1 min-w-0">
                        <p className={`text-sm font-medium ${isActive ? 'text-[var(--color-text)]' : 'text-[var(--color-text-muted)]'}`}>
                          {source.provider}
                        </p>
                        {source.description && (
                          <p className="text-[11px] text-[var(--color-text-muted)] mt-0.5">{source.description}</p>
                        )}
                      </div>

                      {/* Toggle */}
                      <button
                        onClick={() => handleToggle(group.processKey, source.provider)}
                        className="shrink-0 transition-colors"
                        title={isActive ? t('settings.sourcesActive') : t('settings.sourcesInactive')}
                      >
                        {isActive ? (
                          <ToggleRight className="w-8 h-8 text-green-500" />
                        ) : (
                          <ToggleLeft className="w-8 h-8 text-[var(--color-text-muted)]" />
                        )}
                      </button>

                      {/* Status label */}
                      <span className={`text-[10px] font-medium px-2 py-0.5 rounded-full shrink-0 ${
                        isActive
                          ? 'text-green-600 bg-green-500/10'
                          : 'text-[var(--color-text-muted)] bg-[var(--color-border)]'
                      }`}>
                        {isActive ? t('settings.sourcesActive') : t('settings.sourcesInactive')}
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
